/**
 * HotelsRiver Duffel extras helpers (resolve SoT).
 * Catalog lives on selectedFlightOffer.availableExtras — never invent services.
 * Selections live in bookingFlow.extras.duffelServiceSelections.
 */

/**
 * @typedef {object} HrFlightExtra
 * @property {string} serviceId
 * @property {"baggage"|"seat"|"other"} type
 * @property {string[]} passengerIds
 * @property {string[]} segmentIds
 * @property {number[]} sliceIndices
 * @property {number|null} maximumQuantity
 * @property {string|null} totalAmount
 * @property {string|null} totalCurrency
 * @property {object} [metadata]
 */

/**
 * @param {unknown} offerOrResolve
 * @returns {HrFlightExtra[]}
 */
export function getAvailableExtras(offerOrResolve) {
  if (!offerOrResolve || typeof offerOrResolve !== "object") return [];
  const list =
    offerOrResolve.availableExtras ||
    offerOrResolve.extras ||
    offerOrResolve.resolveMeta?.extras ||
    null;
  return Array.isArray(list) ? list.filter((e) => e && e.serviceId) : [];
}

/**
 * @param {HrFlightExtra[]} extras
 * @returns {HrFlightExtra[]}
 */
export function filterBaggageExtras(extras) {
  return (Array.isArray(extras) ? extras : []).filter(
    (e) => String(e?.type || "").toLowerCase() === "baggage"
  );
}

/**
 * @param {object|null|undefined} baggage — offer.baggage { carryOn, checked }
 */
export function buildIncludedBaggageLines(baggage) {
  if (!baggage || typeof baggage !== "object") return [];
  const lines = [];
  const carry = baggage.carryOn;
  const checked = baggage.checked;
  if (carry != null && Number(carry) > 0) {
    lines.push(
      Number(carry) === 1
        ? "כבודת יד / טרולי כלולה"
        : `${Number(carry)} יחידות כבודת יד כלולות`
    );
  }
  if (checked != null && Number(checked) > 0) {
    lines.push(
      Number(checked) === 1
        ? "מזוודה רשומה כלולה"
        : `${Number(checked)} מזוודות רשומות כלולות`
    );
  }
  return lines;
}

/**
 * Label for slice index (0 outbound / 1 inbound).
 * @param {number} sliceIndex
 * @param {number} sliceCount
 */
export function sliceDirectionLabelHe(sliceIndex, sliceCount) {
  const n = Number(sliceCount) || 1;
  const i = Number(sliceIndex);
  if (n >= 2) {
    if (i === 0) return "הלוך";
    if (i === 1) return "חזור";
  }
  return "טיסה";
}

/**
 * Group baggage extras by slice index. Services spanning multiple slices
 * appear under each slice. Services with empty sliceIndices go under -1 (general).
 *
 * @param {HrFlightExtra[]} baggageExtras
 * @returns {Map<number, HrFlightExtra[]>}
 */
export function groupBaggageExtrasBySlice(baggageExtras) {
  const map = new Map();
  for (const extra of Array.isArray(baggageExtras) ? baggageExtras : []) {
    const indices =
      Array.isArray(extra.sliceIndices) && extra.sliceIndices.length > 0
        ? extra.sliceIndices
        : [-1];
    for (const idx of indices) {
      const key = Number.isFinite(Number(idx)) ? Number(idx) : -1;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(extra);
    }
  }
  return map;
}

/**
 * @param {HrFlightExtra} extra
 * @param {string} passengerId
 */
export function extraAppliesToPassenger(extra, passengerId) {
  const pid = String(passengerId || "").trim();
  if (!pid || !extra) return false;
  const ids = Array.isArray(extra.passengerIds) ? extra.passengerIds : [];
  if (ids.length === 0) return true; // rare: unrestricted
  return ids.map(String).includes(pid);
}

/**
 * Human title for a baggage extra row.
 * @param {HrFlightExtra} extra
 */
export function baggageExtraTitleHe(extra) {
  const meta = extra?.metadata && typeof extra.metadata === "object" ? extra.metadata : {};
  const bagType = String(meta.baggageType || "").toLowerCase();
  const weight = meta.maximumWeightKg;
  const weightPart =
    weight != null && Number.isFinite(Number(weight))
      ? ` עד ${Number(weight)} ק״ג`
      : "";
  if (bagType === "carry_on" || bagType === "carry-on") {
    return `טרולי / כבודת יד${weightPart}`;
  }
  if (bagType === "checked") {
    return `מזוודה רשומה${weightPart}`;
  }
  return `כבודה נוספת${weightPart}`;
}

/**
 * @param {string|null|undefined} amount
 * @param {string|null|undefined} currency
 */
export function formatExtraUnitPrice(amount, currency) {
  if (amount == null || amount === "") return "";
  const n = Number(amount);
  const num = Number.isFinite(n)
    ? Math.abs(n - Math.round(n)) < 0.01
      ? String(Math.round(n))
      : n.toFixed(2)
    : String(amount);
  const cur = String(currency || "").trim().toUpperCase();
  if (cur === "USD" || cur === "$") return `+${num}$`;
  if (cur === "ILS" || cur === "NIS" || cur === "₪") return `+${num} ₪`;
  return cur ? `+${num} ${cur}` : `+${num}`;
}

/**
 * Selection map: serviceId → { quantity, passengerId? }
 * When a service is already bound to one passengerId, quantity alone is enough.
 *
 * @param {object} selections
 * @param {HrFlightExtra[]} availableExtras
 * @returns {{ amount: number, currency: string|null }}
 */
export function computeDuffelExtrasTotal(selections, availableExtras) {
  const sel = selections && typeof selections === "object" ? selections : {};
  const catalog = Array.isArray(availableExtras) ? availableExtras : [];
  const byId = new Map(catalog.map((e) => [String(e.serviceId), e]));
  let amount = 0;
  let currency = null;
  for (const [serviceId, row] of Object.entries(sel)) {
    const qty = Number(row?.quantity);
    if (!Number.isFinite(qty) || qty <= 0) continue;
    const extra = byId.get(String(serviceId));
    if (!extra) continue;
    const unit = Number(extra.totalAmount);
    if (!Number.isFinite(unit)) continue;
    amount += unit * qty;
    if (!currency && extra.totalCurrency) {
      currency = String(extra.totalCurrency).trim().toUpperCase();
    }
  }
  return { amount, currency };
}

/**
 * Clamp quantity into [0, maximumQuantity] (null max → treat as 1).
 * @param {HrFlightExtra} extra
 * @param {number} quantity
 */
export function clampExtraQuantity(extra, quantity) {
  const maxRaw = extra?.maximumQuantity;
  const max =
    maxRaw == null || maxRaw === ""
      ? 1
      : Math.max(0, Number(maxRaw) || 0);
  const q = Number(quantity);
  if (!Number.isFinite(q) || q <= 0) return 0;
  return Math.min(Math.floor(q), max);
}

/**
 * Build passenger list for extras UI from Duffel slots / booking passengers.
 * @param {object[]} passengers
 * @returns {{ id: string, label: string, role: string }[]}
 */
export function mapDuffelExtrasPaxList(passengers) {
  const arr = Array.isArray(passengers) ? passengers : [];
  return arr
    .map((p, index) => {
      const id = String(p?.duffelPassengerId || p?.passengerId || "").trim();
      if (!id) return null;
      const first = String(p?.firstName || "").trim();
      const last = String(p?.lastName || "").trim();
      const name = [first, last].filter(Boolean).join(" ");
      const type = String(p?.passengerType || p?.type || "").toLowerCase();
      const role =
        type.includes("infant")
          ? "infant"
          : type.includes("child")
            ? "child"
            : "adult";
      return {
        id,
        label: name || `נוסע ${index + 1}`,
        role,
        type,
      };
    })
    .filter(Boolean);
}
